import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useLocalization } from "@fluent/react";
import {
  Alert,
  CollapsibleCard,
  ContentCard,
  ResponsiveFlex,
  Button,
  Row,
  Spinner,
  TextInput,
  Toggle,
} from "@narmi/design_system";
import { entities } from "byzantine";
import {
  POSTAL_CODE_NOT_APPLICABLE_COUNTRIES,
  SWIFT_DESTINATION_COUNTRIES,
  useFetchRecipientAddressRenameFields,
  useFetchSwiftAdditionalDetails,
  useFetchSwiftAdditionalFieldsByCountry,
  useSwiftWireDestinationForm,
} from "byzantine/src/dbbl/businessLogic/entities/recipients";
import type { SwiftDestinationFormType } from "byzantine/src/dbbl/businessLogic/entities/recipients";
import {
  catcat as cc,
  InternationalAddressAutocompleteInput,
  useNotificationContext,
} from "cerulean";
import CountrySelector from "../form/CountrySelector";
import SectionHeader from "./SectionHeader";
import { useDrawerContext } from "./DrawerValidationsProvider";
import styles from "./RecipientDrawer.module.scss";
import InternationalPhoneInput from "../../src/modules/recipientDrawer/sections/paymentMethods/internationalWire/InternationalPhoneInput";
import { SectionProps } from ".";

type BicSectionState = {
  additionalDetails: API.SwiftDestinationAdditionalField[] | null;
  renameFields: { [key: string]: string } | null;
  institutionDetails: { [key: string]: string } | null;
};

const INITIAL_BIC_STATE: BicSectionState = {
  additionalDetails: null,
  renameFields: {},
  institutionDetails: {},
};

const InternationalWireSection = ({
  title,
  optionalLabel,
  isOpen,
  onClose,
  onOpen,
  onSave,
  recipientId,
  isEditDrawer = false,
  fromInternationalWireFlow = false,
}: SectionProps) => {
  const { l10n } = useLocalization();
  const recipient = entities.recipients.useRecipient(recipientId);
  const { triggerValidations } = useDrawerContext();
  const [showCancelButton, setShowCancelButton] = useState(false);
  const { sendNotification } = useNotificationContext();
  const {
    handleSubmit,
    control,
    reset,
    getValues,
    setValue,
    handleCreate,
    formErrors,
    setFormErrors,
    trigger,
    watch,
  } = useSwiftWireDestinationForm(recipientId);
  const {
    fetchSwiftAdditionalDetails: fetchPrimaryBicDetails,
    errorMessage: bicErrorMessage,
    loading: loadingBIC,
  } = useFetchSwiftAdditionalDetails();
  const {
    fetchSwiftAdditionalDetails: fetchCorrespondentBicDetails,
    errorMessage: correspondentBicErrorMessage,
    loading: loadingCorrespondentBIC,
  } = useFetchSwiftAdditionalDetails();
  const {
    fetchRecipientAddressRenameFields,
    errorMessage: addressRenameErrorMessage,
  } = useFetchRecipientAddressRenameFields();
  const { fetchSwiftAdditionalFieldsByCountry } =
    useFetchSwiftAdditionalFieldsByCountry();

  const [primaryBicState, setPrimaryBicState] =
    useState<BicSectionState>(INITIAL_BIC_STATE);
  const [correspondentBicState, setCorrespondentBicState] =
    useState<BicSectionState>(INITIAL_BIC_STATE);
  const [addressRenameFields, setAddressRenameFields] = useState<{
    [key: string]: string;
  } | null>(null);
  const [branchAddressRenameFields, setBranchAddressRenameFields] = useState<{
    [key: string]: string;
  } | null>(null);

  const isCorrespondentBankEnabled = watch("isCorrespondentBankEnabled");
  const shouldShowAlert =
    !recipient?.swift_destination && fromInternationalWireFlow;

  const resetAdditionalDetailValues = (preserveFields: string[] = []) => {
    const resetValues: Record<string, any> = {
      renameFields: {},
      additionalFieldsToShow: {},
      account_identifier: "",
      branch_address: undefined,
      metadata: {
        iban: getValues("metadata.iban"),
        routing_number: "",
        phone: "",
        itin: "",
        bank_id: "",
        bank_name: "",
      },
    };
    preserveFields.forEach((field) => {
      delete resetValues[field];
    });
    reset({
      ...getValues(),
      ...resetValues,
    });
  };

  const setPrefilledAdditionalFields = (res: API.SwiftAdditionalDetails) => {
    const fieldNames = new Set(res.additional_fields.map((f) => f.name));
    if (
      fieldNames.has("branch_address") &&
      res.address &&
      !getValues("branch_address")
    ) {
      setValue("branch_address", res.address);
    }
    if (
      fieldNames.has("routing_number") &&
      res.swift_routing_code &&
      !getValues("metadata.routing_number")
    ) {
      setValue("metadata.routing_number", res.swift_routing_code);
    }
  };

  const setAdditionalFieldsToShow = (
    additionalFields: API.SwiftDestinationAdditionalField[]
  ) => {
    const fieldsToShow = Object.fromEntries(
      additionalFields.map((field) => [field.name, true])
    );
    setValue("additionalFieldsToShow", fieldsToShow);
  };

  const applyBicResponse = (res: API.SwiftAdditionalDetails) => {
    setPrimaryBicState({
      additionalDetails: res.additional_fields,
      renameFields: res.rename_fields,
      institutionDetails: {
        institutionLocation: res.institution_location,
        institutionName: res.institution_name,
      },
    });
    setValue("renameFields", res.rename_fields || {});
    setAdditionalFieldsToShow(res.additional_fields);
    setPrefilledAdditionalFields(res);
  };

  const fetchAndSetBicDetails = async (bic: string) => {
    setPrimaryBicState((prev) => ({
      ...prev,
      institutionDetails: INITIAL_BIC_STATE.institutionDetails,
    }));
    const res = await fetchPrimaryBicDetails(bic);
    if (res?.additional_fields) {
      resetAdditionalDetailValues();
      applyBicResponse(res);
    }
    return res;
  };

  const handlePrimaryBicOnBlur = async (
    event: React.FocusEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    if (isCorrespondentBankEnabled) {
      // When correspondent bank is enabled, skip the BIC endpoint
      return;
    }
    if (value) {
      await fetchAndSetBicDetails(value);
    } else {
      resetAdditionalDetailValues();
      setPrimaryBicState(INITIAL_BIC_STATE);
    }
  };

  const handleCorrespondentBicOnBlur = async (
    event: React.FocusEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    if (value) {
      setCorrespondentBicState(INITIAL_BIC_STATE);
      const res = await fetchCorrespondentBicDetails(value);
      if (res) {
        setCorrespondentBicState({
          additionalDetails: null,
          renameFields: {},
          institutionDetails: {
            institutionLocation: res.institution_location,
            institutionName: res.institution_name,
          },
        });
      } else {
        setCorrespondentBicState(INITIAL_BIC_STATE);
      }
    } else {
      setCorrespondentBicState(INITIAL_BIC_STATE);
    }
  };

  const onValid = (values: SwiftDestinationFormType) => {
    const onSuccess = () => {
      setShowCancelButton(true);
      onSave();
    };
    const onError = async (error: any) => {
      let errorMessage = l10n.getString("error-form-generic");
      if (error?.response) {
        try {
          const errorBody = await error.response.json();
          if (typeof errorBody === "object") {
            setFormErrors(errorBody);
          }
        } catch {
          // Ignore JSON parse errors and fall back to generic/message-based error handling.
        }
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (typeof error === "string") {
        errorMessage = error;
      }
      sendNotification({
        type: "negative",
        text: errorMessage,
      });
    };
    if (recipient) {
      // TODO: handle update case. Do we want a patch/put endpoint for this?
      handleCreate(values, onSuccess, onError);
    }
  };

  const handleClickOpen = () => {
    const { isValid: isDrawerValid } = triggerValidations();
    if (!isDrawerValid) {
      return;
    }
    onOpen();
  };

  const handleClickClose = () => {
    const { isValid: isDrawerValid } = triggerValidations();
    if (!isDrawerValid) {
      return;
    }
    onClose();
  };

  const handleClickCancel = () => {
    handleClickClose();
  };

  const fetchAndSetRecipientAddressRenameFields = async (
    countryCode: string,
    setter: (
      fields: { [key: string]: string } | null
    ) => void = setAddressRenameFields
  ) => {
    if (!countryCode) {
      setter(null);
      return;
    }
    const res = await fetchRecipientAddressRenameFields(countryCode);
    if (res) {
      setter(res);
    } else {
      setter(null);
    }
  };

  const fetchAndSetAdditionalFieldsByCountry = async (countryCode: string) => {
    const res = await fetchSwiftAdditionalFieldsByCountry(countryCode);
    if (res?.additional_fields) {
      setPrimaryBicState({
        additionalDetails: res.additional_fields,
        renameFields: res.rename_fields,
        institutionDetails: null,
      });
      setValue("renameFields", res.rename_fields || {});
      setAdditionalFieldsToShow(res.additional_fields);
    }
  };

  const handleBranchAddressCountryChange = async (countryCode: string) => {
    // reset additional fields, but preserve branch_address since we need to retain the country_code
    // otherwise changing country_code would reset branch_address and reset the whole form
    resetAdditionalDetailValues(["branch_address"]);
    if (countryCode) {
      await fetchAndSetAdditionalFieldsByCountry(countryCode);
    }
  };

  const onAddressAutoCompleteChange = (
    update: Partial<API.Address>,
    fieldName: "address" | "branch_address" = "address"
  ) => {
    reset({
      ...getValues(),
      [fieldName]: {
        ...getValues(fieldName),
        ...update,
      },
    });
  };

  const handleCorrespondentBankToggleChange = (isActive: boolean) => {
    if (isActive) {
      // Switching ON: clear bic and reset additional details
      setValue("bic", "");
      resetAdditionalDetailValues(["branch_address"]);
      // when toggling on, set a default state for branch_address so validation doesn't get weird about undefined, null values
      if (!getValues("branch_address")) {
        setValue("branch_address", {
          country_code: "",
          street_address: "",
          street_address_2: "",
          city: "",
          region_code: "",
          postal_code: "",
        });
      }
      setPrimaryBicState(INITIAL_BIC_STATE);
    } else {
      // Switching OFF: clear correspondent state and bank fields, re-fetch via BIC
      setValue("correspondent_bank_bic", "");
      setValue("metadata.bank_id", "");
      setValue("metadata.bank_name", "");
      setCorrespondentBicState(INITIAL_BIC_STATE);
      const bic = getValues("bic");
      if (bic) {
        fetchAndSetBicDetails(bic);
      } else {
        resetAdditionalDetailValues();
        setPrimaryBicState(INITIAL_BIC_STATE);
      }
    }
  };

  // --- Effects ---

  useEffect(() => {
    if (recipient?.swift_destination?.id) {
      setShowCancelButton(true);
    }
  }, []);

  // Fetch BIC details and correspondent institution details on mount for edit mode / after creation
  useEffect(() => {
    const fetchDetails = async () => {
      const bic = recipient?.swift_destination?.bic;
      const correspondentBic =
        recipient?.swift_destination?.correspondent_bank_bic;

      if (correspondentBic) {
        // Correspondent bank is enabled: use branch_address country for additional_fields
        const countryCode =
          recipient?.swift_destination?.branch_address?.country_code;
        const [, correspondentRes] = await Promise.all([
          countryCode
            ? fetchAndSetAdditionalFieldsByCountry(countryCode)
            : Promise.resolve(),
          fetchCorrespondentBicDetails(correspondentBic),
        ]);
        if (correspondentRes) {
          setCorrespondentBicState({
            additionalDetails: null,
            renameFields: {},
            institutionDetails: {
              institutionLocation: correspondentRes.institution_location,
              institutionName: correspondentRes.institution_name,
            },
          });
        } else {
          setCorrespondentBicState(INITIAL_BIC_STATE);
        }
      } else {
        setCorrespondentBicState(INITIAL_BIC_STATE);
        if (bic) {
          // No correspondent bank: use bic endpoint as normal
          const res = await fetchPrimaryBicDetails(bic);
          if (res?.additional_fields) {
            applyBicResponse(res);
          }
        }
      }

      // After all fields are set up, validate and auto-open if invalid (edit mode only)
      if (isEditDrawer && recipient?.swift_destination) {
        const isValid = await trigger();
        if (!isValid) {
          onOpen();
        }
      }
    };
    if (isEditDrawer || recipient?.swift_destination) {
      fetchDetails();
    }
  }, [recipient?.swift_destination?.id]);

  // --- Render helpers ---

  const renderAddressSection = ({
    sectionHeader,
    fieldName,
    key,
    fieldError,
  }: {
    sectionHeader: string;
    fieldName: "address" | "branch_address";
    key?: string;
    fieldError?: any;
  }) => (
    <div
      className={cc({
        "margin--top--l":
          fieldName === "branch_address" && !isCorrespondentBankEnabled,
      })}
      key={key}
    >
      <ContentCard kind="plain" paddingSize="none">
        <p className={styles.sectionSubHeader}>{sectionHeader}</p>
        <div className="margin--bottom--l">
          <Controller
            name={`${fieldName}.country_code`}
            control={control as any}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <CountrySelector
                label="Country"
                countries={SWIFT_DESTINATION_COUNTRIES}
                onChange={(e) => {
                  const renameSetter =
                    fieldName === "branch_address"
                      ? setBranchAddressRenameFields
                      : setAddressRenameFields;
                  fetchAndSetRecipientAddressRenameFields(e, renameSetter);
                  onChange(e);
                  if (fieldName === "branch_address") {
                    handleBranchAddressCountryChange(e);
                  }
                }}
                value={(value as string) ?? ""}
                error={
                  error?.message ||
                  fieldError?.country_code ||
                  addressRenameErrorMessage
                }
              />
            )}
          />
        </div>
        <Row gapSize="m" className="margin--bottom--l">
          <Row.Item shrink className={styles.addressGrow}>
            <InternationalAddressAutocompleteInput
              control={control as any}
              name={`${fieldName}.street_address`}
              label={l10n.getString("label-street-address")}
              onUpdate={(update: Partial<API.Address>) =>
                onAddressAutoCompleteChange(update, fieldName)
              }
              isInternational={true}
              error={fieldError?.street_address}
            />
          </Row.Item>
          <Row.Item shrink className={styles.addressShrink}>
            <Controller
              name={`${fieldName}.street_address_2`}
              control={control as any}
              render={({ field, fieldState: { error } }) => (
                <TextInput
                  {...field}
                  value={field.value || ""}
                  field={`${fieldName}.street_address_2`}
                  label={l10n.getString("label-address-2")}
                  error={error?.message || fieldError?.street_address_2}
                />
              )}
            />
          </Row.Item>
        </Row>
        <div className="margin--bottom--l">
          <Controller
            name={`${fieldName}.city`}
            control={control as any}
            render={({ field, fieldState: { error } }) => (
              <TextInput
                {...field}
                value={field.value || ""}
                field={`${fieldName}.city`}
                label={l10n.getString("label-city")}
                error={
                  error?.message ||
                  fieldError?.non_field_errors ||
                  fieldError?.city
                }
              />
            )}
          />
        </div>
        <Row gapSize="m">
          <Row.Item shrink className={styles.addressGrow}>
            <Controller
              name={`${fieldName}.region_code`}
              control={control as any}
              render={({ field, fieldState: { error } }) => (
                <TextInput
                  {...field}
                  value={field.value || ""}
                  field={`${fieldName}.region_code`}
                  label={
                    (fieldName === "branch_address"
                      ? branchAddressRenameFields
                      : addressRenameFields
                    )?.region_code || l10n.getString("label-region-code")
                  }
                  error={error?.message || fieldError?.region_code}
                />
              )}
            />
          </Row.Item>
          {!POSTAL_CODE_NOT_APPLICABLE_COUNTRIES.has(
            watch(`${fieldName}.country_code`) || ""
          ) && (
            <Row.Item shrink className={styles.addressShrink}>
              <Controller
                name={`${fieldName}.postal_code`}
                control={control as any}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { error },
                }) => (
                  <TextInput
                    onChange={onChange}
                    value={value || ""}
                    onBlur={onBlur}
                    field={`${fieldName}.postal_code`}
                    label={l10n.getString("label-postal-code")}
                    error={error?.message || fieldError?.postal_code}
                  />
                )}
              />
            </Row.Item>
          )}
        </Row>
      </ContentCard>
    </div>
  );

  const renderInstitutionDetails = ({
    loading,
    details,
  }: {
    loading: boolean;
    details: { [key: string]: string } | null;
  }) => {
    if (loading) {
      return (
        <Row className={"margin--top--xs"}>
          <Spinner size={22} color="var(--theme-secondary)" />
        </Row>
      );
    }
    if (details?.institutionName) {
      return (
        <Row className={`margin--top--xs ${styles.institutionDetails}`}>
          <Row.Item className={styles.bankNameContainer}>
            <span className="narmi-icon-bank"></span>
            <span
              className={`fontColor--secondary ${styles.bankName}`}
              title={details.institutionName}
            >
              {details.institutionName}
            </span>
          </Row.Item>
          <Row.Item shrink>
            <span className="fontColor--secondary">
              {details.institutionLocation}
            </span>
          </Row.Item>
        </Row>
      );
    }
    return null;
  };

  const mapAdditionalDetails = (errors: any) => {
    if (!primaryBicState.additionalDetails?.length) {
      return null;
    }
    return primaryBicState.additionalDetails.map((detail) => {
      if (detail.name === "branch_address") {
        return renderAddressSection({
          sectionHeader: l10n.getString(
            "international-wire-drawer-branch-address"
          ),
          fieldName: "branch_address",
          key: detail.name,
          fieldError: (errors as any)?.[detail.name],
        });
      }
      return (
        <div className="margin--top--l" key={detail.name}>
          <Controller
            name={`metadata.${detail.name}`}
            control={control as any}
            render={({ field, fieldState: { error } }) => {
              const fieldError =
                error?.message ||
                errors?.[
                  detail.name as keyof API.CreateUpdateSwiftDestination.CreateUpdateSwiftValidationError
                ]?.[0];
              if (detail.name === "phone") {
                return (
                  <InternationalPhoneInput
                    onChange={field.onChange}
                    value={field.value}
                    error={fieldError}
                    bic={getValues("bic") || ""}
                  />
                );
              }
              const label =
                detail.name === "bank_id"
                  ? l10n.getString("international-wire-drawer-bank-id-optional")
                  : primaryBicState.renameFields?.[detail.name] || detail.name;
              return (
                <TextInput
                  {...field}
                  label={label}
                  value={field.value || ""}
                  error={fieldError}
                />
              );
            }}
          />
        </div>
      );
    });
  };

  const renderBankDetailsSection = ({
    sectionHeader,
    errors,
    loading,
    bicError,
    onBicBlur,
  }: {
    sectionHeader: string;
    errors: any;
    loading: boolean;
    bicError: string | undefined;
    onBicBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  }) => (
    <ContentCard kind="plain" paddingSize="none">
      <p className={styles.sectionSubHeader}>{sectionHeader}</p>
      {!isCorrespondentBankEnabled && (
        <>
          <div>
            <Controller
              name="bic"
              control={control as any}
              render={({ field, fieldState: { error } }) => (
                <TextInput
                  {...field}
                  value={field.value || ""}
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                    field.onBlur();
                    onBicBlur(e);
                  }}
                  field="bic"
                  label={l10n.getString("swift-bic")}
                  error={error?.message || bicError}
                />
              )}
            />
          </div>
          {renderInstitutionDetails({
            loading,
            details: primaryBicState.institutionDetails,
          })}
        </>
      )}
      {isCorrespondentBankEnabled && (
        <>
          <div>
            <Controller
              name="metadata.bank_name"
              control={control as any}
              render={({ field, fieldState: { error } }) => (
                <TextInput
                  {...field}
                  value={field.value || ""}
                  field="metadata.bank_name"
                  label={l10n.getString("international-wire-drawer-bank-name")}
                  error={error?.message}
                />
              )}
            />
          </div>
          <div className="margin--top--l">
            <Controller
              name="metadata.bank_id"
              control={control as any}
              render={({ field, fieldState: { error } }) => (
                <TextInput
                  {...field}
                  value={field.value || ""}
                  field="metadata.bank_id"
                  label={l10n.getString(
                    "international-wire-drawer-bank-id-optional"
                  )}
                  error={error?.message || formErrors?.bank_id?.[0]}
                />
              )}
            />
          </div>
        </>
      )}
      <div className="margin--top--l">
        <Controller
          name="account_identifier"
          control={control as any}
          render={({ field, fieldState: { error } }) => {
            if (
              primaryBicState.renameFields?.account_identifier ===
              "Phone number"
            ) {
              return (
                <InternationalPhoneInput
                  onChange={field.onChange}
                  value={field.value}
                  error={
                    error?.message ||
                    errors?.[
                      "account_identifier" as keyof API.CreateUpdateSwiftDestination.CreateUpdateSwiftValidationError
                    ]?.[0]
                  }
                  bic={getValues("bic") || ""}
                />
              );
            }
            return (
              <TextInput
                {...field}
                value={field.value || ""}
                field="account_identifier"
                label={
                  primaryBicState.renameFields?.["account_identifier"] ||
                  "Account identifier"
                }
                error={
                  error?.message ||
                  errors?.[
                    "account_identifier" as keyof API.CreateUpdateSwiftDestination.CreateUpdateSwiftValidationError
                  ]?.[0]
                }
              />
            );
          }}
        />
      </div>
      {mapAdditionalDetails(errors)}
    </ContentCard>
  );

  const renderCorrespondentBankToggle = () => (
    <Row gapSize="s" alignItems="center">
      <Controller
        name="isCorrespondentBankEnabled"
        control={control as any}
        render={({ field }) => (
          <Toggle
            isActive={field.value}
            onChange={(isActive: boolean) => {
              field.onChange(isActive);
              handleCorrespondentBankToggleChange(isActive);
            }}
            labelledBy="correspondent-bank-toggle-label"
          />
        )}
      />
      <label id="correspondent-bank-toggle-label" className="fontWeight--bold">
        {l10n.getString("international-wire-drawer-correspondent-bank-toggle")}
      </label>
    </Row>
  );

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit(onValid, console.error)();
  };

  return (
    <CollapsibleCard
      onClose={handleClickClose}
      onOpen={handleClickOpen}
      isOpen={isOpen}
      title={title}
      trigger="header"
      renderTitle={() => (
        <SectionHeader
          title={title || "International wire"}
          optionalLabel={optionalLabel}
          isOpen={isOpen}
          checkCondition={Boolean(
            getValues("bic") || getValues("correspondent_bank_bic")
          )}
        />
      )}
    >
      <form onSubmit={onSubmit} noValidate>
        <ResponsiveFlex gapSize="xl">
          <Alert
            kind="warn"
            isActive={shouldShowAlert}
            icon="alert-triangle"
            isDismissable={false}
          >
            <div className="fontSize--s">
              {l10n.getString("add-swift-details-alert")}
            </div>
          </Alert>
          <div
            className={cc({
              [styles.collapseGap]: !shouldShowAlert,
            })}
          >
            {renderAddressSection({
              sectionHeader: l10n.getString(
                "international-wire-drawer-recipient-address"
              ),
              fieldName: "address",
              fieldError: (formErrors as any)?.address,
            })}
          </div>
          {renderCorrespondentBankToggle()}
          {isCorrespondentBankEnabled &&
            renderAddressSection({
              sectionHeader: l10n.getString(
                "international-wire-drawer-beneficiary-bank-address"
              ),
              fieldName: "branch_address",
              fieldError: (formErrors as any)?.branch_address,
            })}
          {renderBankDetailsSection({
            sectionHeader: l10n.getString(
              "international-wire-drawer-beneficiary-bank-details"
            ),
            errors: formErrors,
            loading: loadingBIC,
            bicError: bicErrorMessage,
            onBicBlur: handlePrimaryBicOnBlur,
          })}
          {isCorrespondentBankEnabled ? (
            <ContentCard kind="plain" paddingSize="none">
              <p className={styles.sectionSubHeader}>
                {l10n.getString(
                  "international-wire-drawer-correspondent-bank-details"
                )}
              </p>
              <Controller
                name="correspondent_bank_bic"
                control={control as any}
                render={({ field, fieldState: { error } }) => (
                  <TextInput
                    {...field}
                    value={field.value || ""}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                      field.onBlur();
                      handleCorrespondentBicOnBlur(e);
                    }}
                    field="correspondent_bank_bic"
                    label={l10n.getString("swift-bic")}
                    error={
                      error?.message ||
                      correspondentBicErrorMessage ||
                      formErrors?.correspondent_bank_bic?.[0]
                    }
                  />
                )}
              />
              {!formErrors?.correspondent_bank_bic &&
                renderInstitutionDetails({
                  loading: loadingCorrespondentBIC,
                  details: correspondentBicState.institutionDetails,
                })}
            </ContentCard>
          ) : null}
          <Row alignItems="center">
            <Row.Item>
              {showCancelButton ? (
                <Button
                  kind="negative"
                  testId="cancel-international-wire"
                  onClick={handleClickCancel}
                >
                  {l10n.getString("button-cancel")}
                </Button>
              ) : null}
            </Row.Item>
            <Row.Item shrink>
              <Button kind="primary" testId="save-international-wire">
                {l10n.getString("button-save")}
              </Button>
            </Row.Item>
          </Row>
        </ResponsiveFlex>
      </form>
    </CollapsibleCard>
  );
};

export default InternationalWireSection;
